import React, { useState, useEffect } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { useWordAudio } from '../../lib/useWordAudio';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import '../impressivespeech/WordFormation.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const Antonyms = ({ ageGroup, onBack, onStartPause, onComplete }) => {
  const { data: wordsData, loading: wordsLoading } = useWordsData();
  const { start: startSpeakRecognition } = useSpeakAnswer('Antonyms');
  const { play: playWordAudio } = useWordAudio();
  const { play: playInstruction } = useInstructionAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);
  const [isSpoken, setIsSpoken] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [highlightedImage, setHighlightedImage] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [hasSpoken, setHasSpoken] = useState(false);
  const [clickedKey, setClickedKey] = useState(null);

  const [selectedMode, setSelectedMode] = useState(null); 
  const [blinkTarget, setBlinkTarget] = useState('speak'); 

  const IMAGE_MAP = {
    'большой_маленький': ['ball', 'ball_3'],
    'быстрый_медленный': ['gepard', 'turtle'],
    'волосатый_лысый': ['hairy', 'bold'],
    'высокий_низкий': ['tall', 'short'],
    'гладкий_шершавый': ['smooth_surface', 'tree_bark'],
    'горячий_холодный': ['tea', 'ice_cream'],
    'громкий_тихий': ['loud', 'quiet'],
    'длинный_короткий': ['long_pencil', 'short_pencil'],
    'добрый_злой': ['kind', 'evil'],
    'друг_враг': ['friend', 'foe'],
    'лёгкий_тяжёлый': ['feather', 'heavy'],
    'мокрый_сухой': ['wet', 'dry'],
    'молодой_старый': ['young_man', 'old_man'],
    'мягкий_твердый': ['pillow', 'stone'],
    'полный_пустой': ['full', 'empty'],
    'слабый_сильный': ['weak', 'strong'],
    'сладкий_горький': ['cake', 'bitter'],
    'тёмный_светлый': ['dark', 'light'],
    'толстый_худой': ['fat', 'skinny'],
    'умный_глупый': ['smart', 'stupid'],
    'чистый_грязный': ['clean', 'dirty'],
    'широкий_узкий': ['wide', 'narrow'],
    
    
  };

  const WORDS_BY_AGE = {
    '4': ['большой_маленький', 'высокий_низкий','длинный_короткий', 'широкий_узкий', 'лёгкий_тяжёлый', 'мокрый_сухой', 'горячий_холодный', 'толстый_худой', 'чистый_грязный', 'тёмный_светлый'],
    '5': ['большой_маленький', 'высокий_низкий','длинный_короткий', 'широкий_узкий', 'лёгкий_тяжёлый', 'мокрый_сухой', 'горячий_холодный', 'толстый_худой', 'чистый_грязный', 'тёмный_светлый'],
    '6': ['быстрый_медленный', 'громкий_тихий', 'мягкий_твердый', 'слабый_сильный', 'сладкий_горький', 'полный_пустой', 'гладкий_шершавый', 'добрый_злой', 'друг_враг', 'тёмный_светлый'],
    '7-9': ['полный_пустой', 'молодой_старый', 'умный_глупый', 'волосатый_лысый', 'чистый_грязный', 'друг_враг'],
    '10-12': ['полный_пустой', 'молодой_старый', 'умный_глупый', 'волосатый_лысый', 'чистый_грязный', 'друг_враг'],
    '13-15': ['полный_пустой', 'молодой_старый', 'умный_глупый', 'волосатый_лысый', 'чистый_грязный', 'друг_враг'],
    '16+': ['полный_пустой', 'молодой_старый', 'умный_глупый', 'волосатый_лысый',  'чистый_грязный', 'друг_враг']
  };

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const generateQuestions = () => {
    if (!wordsData) return [];
    const wordsForAge = WORDS_BY_AGE[ageGroup] || WORDS_BY_AGE['4'];
    return wordsForAge
      .filter(wordKey => wordsData.antonyms[wordKey])
      .map(wordKey => {
        const wordData = wordsData.antonyms[wordKey];
        const imgs = IMAGE_MAP[wordKey] || ['placeholder', 'placeholder'];
        const additionalCorrectWords = (wordData.additional_correct_words || '')
          .split(',')
          .map(w => w.trim())
          .filter(Boolean);
        return {
          id: wordKey,
          singular: wordData.word_a,
          plural: wordData.word_b,
          singularAudioUrl: wordData.word_a_audio_url ?? null,
          additionalCorrectWords,
          images: {
            singular: `${S3_BASE_URL}/${imgs[0]}.png`,
            plural: `${S3_BASE_URL}/${imgs[1]}.png`
          },
          options: ['singular', 'plural']
        };
      });
  };

  const questions = generateQuestions();
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  if (currentQuestion && shuffledOptions.length === 0) {
    setShuffledOptions(shuffleArray(currentQuestion.options));
  }

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
  };

  const goToNextQuestion = () => {
    setIsSpeaking(false); 
    setHighlightedImage(null); 
    setIsProcessing(false); 
    setHasSpoken(false);
    setClickedKey(null);
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1); 
        setShuffledOptions([]); 
        setIsSpoken(false); 
        setIsListening(false); 
        setIsSpeaking(false); 
        setIsCorrect(null);
      } else {
        setShowResult(true);
      }
    }, 800);
  };

  const handleImageClick = (key) => {
    if (selectedMode !== 'show' || !isSpeaking || isProcessing) return;
    setIsProcessing(true);
    setClickedKey(key);
    const correct = key === 'singular'; 
    setIsCorrect(correct);
    setAnswers(prev => [...prev, correct]);
    setTimeout(() => goToNextQuestion(), 800);
  };

  const speakWord = () => {
    if (isSpoken || !currentQuestion || hasSpoken) return;
    setHasSpoken(true); 
    setIsSpoken(true); 
    setIsCorrect(null);
    setClickedKey(null);
    setRecognizerReady(false);

    if (selectedMode === 'speak') {
      setHighlightedImage('singular');
    } else {
      setHighlightedImage(null);
    }

    // В режиме "Говорить" распознаватель запускается одновременно с озвучкой
    // вопроса, а не только после её окончания — иначе он не успевал
    // подключить микрофон к моменту, когда ребёнок начинал отвечать.
    if (selectedMode === 'speak') {
      setIsProcessing(true);
      startSpeakRecognition(
        [
          { key: 'singular', text: currentQuestion.singular },
          { key: 'plural', text: currentQuestion.plural },
          // Extra accepted synonyms for the antonym (e.g. "короткий" as well
          // as "маленький") — mapped to the same 'plural' key so any of them
          // resolves as correct, without adding extra images to click.
          ...currentQuestion.additionalCorrectWords.map(word => ({ key: 'plural', text: word })),
        ],
        {
          onListening: () => setRecognizerReady(true),
          onResult: ({ matchedKey }) => {
            setIsProcessing(false);
            const isAnswerCorrect = matchedKey === 'plural';
            setIsCorrect(isAnswerCorrect);
            setAnswers(prev => [...prev, isAnswerCorrect]);
            goToNextQuestion();
          },
        }
      );
    }

    setIsListening(true);
    const onAudioEnd = () => {
      setIsListening(false);
      setIsSpeaking(true);
      if (selectedMode === 'speak') {
        setHighlightedImage('plural');
      }
    };

    if (selectedMode === 'speak') {
      // В режиме "Говорить" сначала звучит фраза-инструкция "Наоборот от
      // слова", затем само слово — два готовых DB-клипа подряд вместо
      // одной склеенной фразы через реальный TTS.
      playInstruction('Наоборот от слова', () => {
        playWordAudio(currentQuestion.singularAudioUrl, onAudioEnd);
      });
    } else {
      // В режиме "Показать" просто называем слово
      playWordAudio(currentQuestion.singularAudioUrl, onAudioEnd);
    }
  };

  useEffect(() => {
    if (selectedMode && currentQuestion && !showResult && !isSpoken && !hasSpoken) {
      const timer = setTimeout(() => speakWord(), 500);
      return () => clearTimeout(timer);
    }
  }, [currentQuestionIndex, selectedMode, showResult]);

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        if (onComplete) {
          onComplete();
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  // === ПЕРЕДАЧА РЕЗУЛЬТАТА В ОТЧЕТ ===
  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
        
        // ВНИМАНИЕ: Здесь подставляем нужный testId и правильный проп (onComplete или onNextModule)
        if (onComplete) onComplete({ testId: 'Antonyms', score: score });
        else onBack();
      }, 4000); // 4 секунды задержки, чтобы показать экран с результатом
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (wordsLoading) return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={0}
      forceStop={true}
      onComplete={onComplete}
    >
      <div className="loading-text">Загрузка...</div>
    </ModuleTemplate>
  );

  if (showResult) {
    const correctCount = answers.filter((a) => a === true).length;
    return (
      <ModuleTemplate
        progress={answers}
        totalQuestions={totalQuestions}
        forceStop={true}
        onComplete={onComplete}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Упражнение завершено!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true}             
      onComplete={onComplete}      
    >
      <div className="loading-text">Выберите режим ниже, чтобы начать</div>
    </ModuleTemplate>
  );

  const displayOptions = shuffledOptions.length > 0 ? shuffledOptions : currentQuestion.options;
  const correctKey = selectedMode === 'show' ? 'singular' : 'plural';

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true}             
      onComplete={onComplete}      
      disabled={!selectedMode}
      extraContent="Антонимы"
    >
      <div className="wordformation-container">
        {!selectedMode && (
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px' }}>
            <button onClick={() => handleModeSelect('speak')} style={{ padding: '14px 28px', borderRadius: '50px', border: `2px solid ${blinkTarget === 'speak' ? '#7C3AED' : '#E5E0F5'}`, background: blinkTarget === 'speak' ? '#EDE9FE' : '#F8F6FF', color: '#1A1A2E', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: blinkTarget === 'speak' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)', fontSize: '18px', transition: 'all 0.3s' }}>Говорить</button>
            <button onClick={() => handleModeSelect('show')} style={{ padding: '14px 28px', borderRadius: '50px', border: `2px solid ${blinkTarget === 'show' ? '#7C3AED' : '#E5E0F5'}`, background: blinkTarget === 'show' ? '#EDE9FE' : '#F8F6FF', color: '#1A1A2E', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: blinkTarget === 'show' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)', fontSize: '18px', transition: 'all 0.3s' }}>Показать</button>
          </div>
        )}
        <div className="wordformation-main-row">
          <div className="wordformation-images-grid">
            {displayOptions.map((key) => {
              const isHighlighted = highlightedImage === key;
              const isAnswerCorrect = isCorrect === true && key === correctKey;
              const isAnswerWrong = isCorrect === false && (selectedMode === 'show' ? key === clickedKey : key === correctKey);
              return (
                <div key={key} className={`wordformation-image-wrapper ${isHighlighted ? 'highlighted' : ''} ${isAnswerCorrect ? 'answer-correct' : ''} ${isAnswerWrong ? 'answer-wrong' : ''}`} onClick={() => handleImageClick(key)} style={{ cursor: selectedMode === 'show' ? 'pointer' : 'default' }}>
                  <img src={currentQuestion.images[key]} alt="" className="wordformation-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                </div>
              );
            })}
          </div>
          <div className="wordformation-indicators">
            <div className={`indicator listen ${isListening ? 'active' : ''}`}><span className="indicator-icon">🎧</span></div>
            <div className={`indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}><span className="indicator-icon">🗣️</span></div>
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default Antonyms;